import { useEffect, useState } from "react";
import type { RadarStateSource } from "@radar-sim/engine";
import type { Area, WorldSnapshot } from "@radar-sim/schema";
import { getTidalAnomalies, isInsidePolygon } from "@radar-sim/area-data";
import { Card, Heading, Stack } from "../ui/primitives.js";

export interface HelmPanelProps {
  source: RadarStateSource;
  paused: boolean;
  onTogglePause: () => void;
  /** Area key — used to look up tidal anomalies for the live drift
   *  indicator. Optional; when absent, no drift readout is shown. */
  area?: Area;
}

const TIME_SCALES = [0.5, 1, 2, 4, 8, 16, 32] as const;

/**
 * Helm + throttle + time-scale panel for solo simulator mode. Lets the
 * student drive the own ship and run the simulation faster or slower
 * than wall-clock. Sends `set_own_cog`, `set_own_sog`, `set_time_scale`,
 * `pause`, `resume` commands through the source.
 *
 * Live values come from the latest snapshot on the source so the
 * controls stay in sync if the world is mutated externally (e.g. an
 * instructor injects a course change in classroom mode).
 */
export function HelmPanel(props: HelmPanelProps): JSX.Element {
  const [snapshot, setSnapshot] = useState<WorldSnapshot | null>(null);
  const [timeScale, setTimeScale] = useState<number>(1);

  useEffect(() => {
    const stop = props.source.subscribe((s) => setSnapshot(s));
    return stop;
  }, [props.source]);

  const cog = snapshot?.ownShip.cog ?? 0;
  const sog = snapshot?.ownShip.sog ?? 0;
  const simSeconds = snapshot?.simTimeSeconds ?? 0;

  const sendCog = (deg: number) => {
    const next = ((deg % 360) + 360) % 360;
    void props.source.sendCommand({ kind: "set_own_cog", cog: next });
  };
  const sendSog = (kn: number) => {
    const next = Math.max(0, Math.min(60, kn));
    void props.source.sendCommand({ kind: "set_own_sog", sog: next });
  };
  const sendScale = (scale: number) => {
    setTimeScale(scale);
    void props.source.sendCommand({ kind: "set_time_scale", scale });
  };
  const dropMob = () => {
    // Inject the MOB drop one tick into the future so the engine sees
    // it via its event queue rather than skipping the latest snapshot.
    void props.source.sendCommand({
      kind: "inject_event",
      timed: {
        atSimSeconds: simSeconds + 0.2,
        event: {
          kind: "mob_drop",
          relativeToOwn: {
            asternNm: 0.05,
            abeamNm: 0,
            abeamSide: "starboard",
          },
        },
      },
    });
  };

  return (
    <Card padding={16} style={{ width: "100%" }}>
      <Stack gap={18}>
        <Heading level={2}>Helm</Heading>

        <CourseControl cog={cog} onChange={sendCog} />
        <SpeedControl sog={sog} onChange={sendSog} />
        <PositionReadout snapshot={snapshot} />
        {props.area !== undefined && (
          <TidalDriftReadout snapshot={snapshot} area={props.area} />
        )}

        <div
          style={{
            borderTop: "1px solid var(--border)",
            paddingTop: 14,
          }}
        >
          <Stack gap={10}>
            <span style={sectionLabel}>Simulator speed</span>
            <Stack direction="row" gap={4} wrap>
              {TIME_SCALES.map((s) => {
                const active = Math.abs(s - timeScale) < 1e-6;
                return (
                  <button
                    key={s}
                    onClick={() => sendScale(s)}
                    aria-pressed={active}
                    aria-label={`${s}× simulator speed`}
                    style={{
                      flex: 1,
                      minWidth: 38,
                      padding: "6px 0",
                      background: active
                        ? "var(--accent)"
                        : "var(--bg-elev)",
                      color: active ? "var(--accent-fg)" : "var(--text)",
                      border: `1px solid ${
                        active ? "var(--accent)" : "var(--border-strong)"
                      }`,
                      borderRadius: "var(--radius-sm)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {s}×
                  </button>
                );
              })}
            </Stack>
            <button
              onClick={props.onTogglePause}
              aria-label={props.paused ? "Resume simulation" : "Pause simulation"}
              style={{
                // Same stop/start colour pattern as the SPEED STOP
                // button: green when the action is "go" (paused → press
                // to resume), red when the action is "stop" (running →
                // press to pause). Reads at a glance like a real
                // start/stop control.
                background: props.paused ? "var(--success)" : "transparent",
                color: props.paused ? "white" : "var(--danger)",
                border: `1px solid ${
                  props.paused ? "var(--success)" : "var(--danger)"
                }`,
                borderRadius: "var(--radius-sm)",
                padding: "9px 14px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {props.paused ? "▶ Resume" : "⏸ Pause"}
            </button>
            <button
              onClick={dropMob}
              aria-label="Drop man overboard SART beacon"
              style={{
                background: "transparent",
                color: "var(--danger)",
                border: "1px solid var(--danger)",
                borderRadius: "var(--radius-sm)",
                padding: "9px 14px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                marginTop: 6,
              }}
            >
              MOB · drop SART
            </button>
          </Stack>
        </div>
      </Stack>
    </Card>
  );
}

function CourseControl(props: {
  cog: number;
  onChange: (deg: number) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<string>(props.cog.toFixed(0));

  useEffect(() => {
    setDraft(props.cog.toFixed(0));
  }, [props.cog]);

  const apply = () => {
    const v = parseFloat(draft);
    if (Number.isFinite(v)) props.onChange(v);
    else setDraft(props.cog.toFixed(0));
  };

  return (
    <Stack gap={10}>
      <Stack direction="row" justify="between" align="center">
        <span style={sectionLabel}>Course</span>
        <span style={readout}>{props.cog.toFixed(0).padStart(3, "0")}°</span>
      </Stack>
      <CompassRing cog={props.cog} onPick={(d) => props.onChange(d)} />
      <Stack direction="row" gap={4}>
        <NudgeBtn label="−10°" onClick={() => props.onChange(props.cog - 10)} />
        <NudgeBtn label="−1°" onClick={() => props.onChange(props.cog - 1)} />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={apply}
          onKeyDown={(e) => {
            if (e.key === "Enter") apply();
          }}
          inputMode="numeric"
          aria-label="Course over ground in degrees"
          style={{
            flex: "1 1 0",
            minWidth: 0,
            background: "var(--bg-elev)",
            color: "var(--text)",
            border: "1px solid var(--border-strong)",
            borderRadius: "var(--radius-sm)",
            padding: "6px 4px",
            fontSize: 13,
            textAlign: "center",
            fontFamily: "var(--font-mono)",
          }}
        />
        <NudgeBtn label="+1°" onClick={() => props.onChange(props.cog + 1)} />
        <NudgeBtn label="+10°" onClick={() => props.onChange(props.cog + 10)} />
      </Stack>
    </Stack>
  );
}

function SpeedControl(props: {
  sog: number;
  onChange: (kn: number) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<string>(props.sog.toFixed(1));

  useEffect(() => {
    setDraft(props.sog.toFixed(1));
  }, [props.sog]);

  const apply = () => {
    const v = parseFloat(draft);
    if (Number.isFinite(v)) props.onChange(v);
    else setDraft(props.sog.toFixed(1));
  };

  return (
    <Stack gap={10}>
      <Stack direction="row" justify="between" align="center">
        <span style={sectionLabel}>Speed</span>
        <span style={readout}>{props.sog.toFixed(1)} kn</span>
      </Stack>
      <input
        type="range"
        min={0}
        max={30}
        step={0.5}
        value={props.sog}
        onChange={(e) => props.onChange(parseFloat(e.target.value))}
        aria-label="Speed over ground in knots"
        style={{ accentColor: "var(--accent)", width: "100%" }}
      />
      <Stack direction="row" gap={4}>
        <NudgeBtn label="−1" onClick={() => props.onChange(props.sog - 1)} />
        <NudgeBtn label="−0.5" onClick={() => props.onChange(props.sog - 0.5)} />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={apply}
          onKeyDown={(e) => {
            if (e.key === "Enter") apply();
          }}
          inputMode="decimal"
          aria-label="Speed in knots"
          style={{
            flex: "1 1 0",
            minWidth: 0,
            background: "var(--bg-elev)",
            color: "var(--text)",
            border: "1px solid var(--border-strong)",
            borderRadius: "var(--radius-sm)",
            padding: "6px 4px",
            fontSize: 13,
            textAlign: "center",
            fontFamily: "var(--font-mono)",
          }}
        />
        <NudgeBtn label="+0.5" onClick={() => props.onChange(props.sog + 0.5)} />
        <NudgeBtn label="+1" onClick={() => props.onChange(props.sog + 1)} />
        <NudgeBtn label="STOP" onClick={() => props.onChange(0)} tone="danger" />
      </Stack>
    </Stack>
  );
}

/**
 * Live position readout — confirms the ship is moving at a glance. On
 * a radar PPI own ship sits at the centre and the world drifts past,
 * so absolute lat/lon is the canonical "are we moving?" indicator. Sim
 * time is shown alongside so the student sees how much scenario time
 * has actually elapsed.
 */
function PositionReadout(props: {
  snapshot: WorldSnapshot | null;
}): JSX.Element {
  const s = props.snapshot;
  const lat = s?.ownShip.position.lat ?? 0;
  const lon = s?.ownShip.position.lon ?? 0;
  const t = s?.simTimeSeconds ?? 0;
  return (
    <Stack gap={8}>
      <span style={sectionLabel}>Position</span>
      <div
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-sm)",
          padding: "10px 12px",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          lineHeight: 1.6,
          color: "var(--text)",
        }}
      >
        <Stack direction="row" justify="between">
          <span style={{ color: "var(--text-muted)" }}>LAT</span>
          <span>{formatLat(lat)}</span>
        </Stack>
        <Stack direction="row" justify="between">
          <span style={{ color: "var(--text-muted)" }}>LON</span>
          <span>{formatLon(lon)}</span>
        </Stack>
        <Stack direction="row" justify="between">
          <span style={{ color: "var(--text-muted)" }}>SIM</span>
          <span>{formatSimTime(t)}</span>
        </Stack>
      </div>
    </Stack>
  );
}

/**
 * Live tidal-drift indicator. When own ship is inside a tidal-anomaly
 * polygon with a declared drift, surface the local set bearing,
 * speed (knots × severity), and the anomaly name. Tells the student
 * exactly why their position is drifting sideways — the same
 * conversation an instructor would have at the chart table when
 * checking a fix off St Catherine's.
 */
function TidalDriftReadout(props: {
  snapshot: WorldSnapshot | null;
  area: Area;
}): JSX.Element | null {
  const s = props.snapshot;
  if (s === null) return null;
  const anomalies = getTidalAnomalies(props.area);
  const here = anomalies.find(
    (a) => a.drift !== undefined && isInsidePolygon(s.ownShip.position, a.polygon),
  );
  if (here === undefined || here.drift === undefined) return null;
  const setDeg = here.drift.bearingDeg;
  const driftKn = here.drift.speedKnots * here.severity;
  // Visual arrow rotated to match the set direction (canvas y points
  // down so subtract 90°).
  const arrowAngle = ((setDeg - 90) * Math.PI) / 180;
  const ax = 14 + 10 * Math.cos(arrowAngle);
  const ay = 14 + 10 * Math.sin(arrowAngle);
  return (
    <Stack gap={6}>
      <span style={{ ...sectionLabel, color: "var(--warn)" }}>
        Tidal set / drift
      </span>
      <div
        style={{
          background: "rgba(245, 158, 11, 0.08)",
          border: "1px solid var(--warn)",
          borderRadius: "var(--radius-sm)",
          padding: "8px 10px",
          display: "flex",
          gap: 10,
          alignItems: "center",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
        }}
      >
        <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
          <line
            x1="14"
            y1="14"
            x2={ax}
            y2={ay}
            stroke="var(--warn)"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="14" cy="14" r="2" fill="var(--warn)" />
        </svg>
        <Stack gap={2} style={{ flex: 1 }}>
          <span style={{ color: "var(--text)", fontSize: 11 }}>
            {here.name}
          </span>
          <Stack direction="row" gap={10}>
            <span>
              <span style={{ color: "var(--text-muted)" }}>SET</span>{" "}
              {setDeg.toFixed(0).padStart(3, "0")}°
            </span>
            <span>
              <span style={{ color: "var(--text-muted)" }}>RATE</span>{" "}
              {driftKn.toFixed(1)} kn
            </span>
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
}

function formatLat(lat: number): string {
  const hemi = lat >= 0 ? "N" : "S";
  const a = Math.abs(lat);
  const deg = Math.floor(a);
  const min = (a - deg) * 60;
  return `${deg.toString().padStart(2, "0")}°${min.toFixed(3).padStart(6, "0")}'${hemi}`;
}
function formatLon(lon: number): string {
  const hemi = lon >= 0 ? "E" : "W";
  const a = Math.abs(lon);
  const deg = Math.floor(a);
  const min = (a - deg) * 60;
  return `${deg.toString().padStart(3, "0")}°${min.toFixed(3).padStart(6, "0")}'${hemi}`;
}
function formatSimTime(seconds: number): string {
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function CompassRing(props: {
  cog: number;
  onPick: (deg: number) => void;
}): JSX.Element {
  const size = 184;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 12;

  const handlePointer = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - cx;
    const y = e.clientY - rect.top - cy;
    const deg = ((Math.atan2(x, -y) * 180) / Math.PI + 360) % 360;
    props.onPick(deg);
  };

  const tipRad = ((props.cog - 90) * Math.PI) / 180;
  const tipX = cx + r * Math.cos(tipRad);
  const tipY = cy + r * Math.sin(tipRad);

  return (
    <svg
      width={size}
      height={size}
      onPointerDown={handlePointer}
      role="slider"
      aria-label="Course compass — drag or click to set heading"
      aria-valuemin={0}
      aria-valuemax={359}
      aria-valuenow={Math.round(props.cog)}
      style={{
        alignSelf: "center",
        cursor: "crosshair",
        touchAction: "none",
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderRadius: "50%",
      }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="var(--border-strong)"
        strokeWidth={1}
      />
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((b) => {
        const rad = ((b - 90) * Math.PI) / 180;
        const x1 = cx + (r - 4) * Math.cos(rad);
        const y1 = cy + (r - 4) * Math.sin(rad);
        const x2 = cx + r * Math.cos(rad);
        const y2 = cy + r * Math.sin(rad);
        return (
          <line
            key={b}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="var(--text-muted)"
            strokeWidth={b % 90 === 0 ? 1.5 : 0.7}
          />
        );
      })}
      {[
        { label: "N", deg: 0 },
        { label: "E", deg: 90 },
        { label: "S", deg: 180 },
        { label: "W", deg: 270 },
      ].map((c) => {
        const rad = ((c.deg - 90) * Math.PI) / 180;
        const lx = cx + (r - 16) * Math.cos(rad);
        const ly = cy + (r - 16) * Math.sin(rad);
        return (
          <text
            key={c.label}
            x={lx}
            y={ly}
            fontSize={11}
            fontWeight={600}
            fill="var(--text-muted)"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {c.label}
          </text>
        );
      })}
      <line
        x1={cx}
        y1={cy}
        x2={tipX}
        y2={tipY}
        stroke="var(--accent)"
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <circle cx={tipX} cy={tipY} r={4} fill="var(--accent)" />
      <circle cx={cx} cy={cy} r={3} fill="var(--text)" />
    </svg>
  );
}

function NudgeBtn(props: {
  label: string;
  onClick: () => void;
  tone?: "default" | "danger";
}): JSX.Element {
  const danger = props.tone === "danger";
  return (
    <button
      onClick={props.onClick}
      aria-label={props.label}
      style={{
        background: "var(--bg-elev)",
        color: danger ? "var(--danger)" : "var(--text)",
        border: `1px solid ${danger ? "var(--danger)" : "var(--border-strong)"}`,
        borderRadius: "var(--radius-sm)",
        // Tight padding + flex:1 so the row of buttons + input can
        // share a narrow helm column (it shrinks when the chart panel
        // is shown alongside) without overflowing.
        padding: "6px 4px",
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
        whiteSpace: "nowrap",
        flex: "1 1 0",
        minWidth: 0,
        textAlign: "center",
      }}
    >
      {props.label}
    </button>
  );
}

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: "uppercase",
  color: "var(--text-muted)",
};

const readout: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 18,
  fontWeight: 700,
  color: "var(--accent)",
};
